import { useContext, useMemo } from 'react';
import styled from '@emotion/styled';
import type { Chat } from '@trinity/chat';
import { AttachmentsContext } from '@trinity/chat-components/components/Chat/context';
import { useMessageTestingRun } from '@trinity/chat-components/hooks/useMessageTestingRun';
import { MessageTestingProgressBar } from '../MessageTestingGrid/ProgressBar';
import { MessageTestingResultsTabs } from '../MessageTestingGrid/ResultsTabs';
import type { TestableMessage, SegmentOption } from '../MessageTestingGrid/types';

interface MessageTestingGridProps {
  id: string;
}

export function MessageTestingGridAttachment({ id }: MessageTestingGridProps): React.JSX.Element | null {
  const attachments = useContext(AttachmentsContext);
  const attachment = useMemo(
    () => attachments.find((x) => x.id === id) as Chat.Attachment.MessageTestingGrid | undefined,
    [attachments, id]
  );

  if (!attachment) return null;

  return <MessageTestingGridInner attachment={attachment} />;
}

function MessageTestingGridInner({
  attachment,
}: {
  attachment: Chat.Attachment.MessageTestingGrid;
}): React.JSX.Element {
  const { runIdentifier, messages: rawMessages, segments: rawSegments, brand, objective } = attachment.data;

  const { status, progress, cells, cellErrors, error, rollups } = useMessageTestingRun(runIdentifier);

  const messages: TestableMessage[] = useMemo(
    () =>
      rawMessages.map((m) => ({
        id: m.id,
        displayTitle: m.value.length > 60 ? m.value.slice(0, 57) + '...' : m.value,
        body: m.value,
        category: m.category,
        brand: brand ?? '',
        code: m.code,
      })),
    [rawMessages, brand]
  );

  const segments: SegmentOption[] = useMemo(
    () => rawSegments.map((s) => ({ identifier: s.identifier, name: s.name })),
    [rawSegments]
  );

  return (
    <Root>
      {(brand || objective) && (
        <RunMeta>
          {brand && (
            <RunMetaItem>
              <strong>Brand:</strong> {brand}
            </RunMetaItem>
          )}
          {objective && (
            <RunMetaItem>
              <strong>Objective:</strong> {objective}
            </RunMetaItem>
          )}
        </RunMeta>
      )}

      <MessageTestingProgressBar status={status} progress={progress} error={error} />

      {(cells.length > 0 || messages.length > 0) && (
        <MessageTestingResultsTabs
          messages={messages}
          segments={segments}
          cells={cells}
          cellErrors={cellErrors}
          rollups={rollups}
        />
      )}
    </Root>
  );
}

// ─── Styled ─────────────────────────────────────────────────────────────────

const Root = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 6px 2px rgba(60, 64, 67, 0.15);
  max-height: 500px;
`;

const RunMeta = styled.div`
  padding: 10px 16px;
  display: flex;
  gap: 20px;
  font-size: 13px;
  color: #333;
  background: #f5f7fa;
  border-bottom: 1px solid #e0e0e0;
`;

const RunMetaItem = styled.span`
  strong {
    color: #555;
    margin-right: 4px;
  }
`;

MessageTestingGridAttachment.displayName = 'Chat.Attachments.MessageTestingGrid';
